import styles from './policyDetails.module.scss';
import { useRouter } from 'next/navigation';
import { ROUTER_GROUPS } from '@/core/constants/routes';
import PolicyDetailsCard from '@/app/(portal)/(policy)/components/policy-details/policyDetailsCard';
import { usePolicyDetails } from './policyDetailsController';
import { useEffect, useMemo } from 'react';
import { PolicyObject } from '@/data/api/policy/types/policyObject.types';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { getAllPoliciesUtil } from '@/core/utils/policy.util';
import { useGeneralContext } from '@/contexts/general/general.context';
import ExpiredCancelledComp from './expiredCancelled/expiredCancelledComp';
import NoPolicyLink from './linkPolicy/noPolicyLink';
import { DecryptedData } from '@/core/utils/encrypt.utils';
import { useGtm } from '@/contexts/analytics/analytics.context';
import { useSuperValue } from '@/hooks/useSuperValue';
import { H6 } from '@/design-system/text/h6/h6';
import { Label } from '@/design-system/text/label/label';
import LinkPolicyComp from './linkPolicy/linkPolicyComp';

export default function PolicyDetails() {
  const router = useRouter();
  const { getPolicyDetails, actionClickHandler } = usePolicyDetails();
  const [policyDetails, setPolicyDetails] = useSuperValue<
    Record<string, PolicyObject[]>
  >({}, true);
  const { dashboardLabels } = useDashboard();
  const { policyDetailsCms } = dashboardLabels;
  const { policies } = useGeneralContext();
  const gtmModule = useGtm();

  useEffect(() => {
    if (!policies.loading) {
      const data = getPolicyDetails();
      setPolicyDetails.setValue(data);
      setPolicyDetails.setLoading(false);
    }
  }, [getPolicyDetails, policies.loading]);

  const activeAndUpcomingPolicies = useMemo(() => {
    return Object.entries(policyDetails.value).filter(([key]) =>
      ['Active', 'Upcoming'].includes(key)
    );
  }, [policyDetails.value]);

  const expiredAndCancelledPolicies = useMemo(() => {
    return Object.entries(policyDetails.value).filter(([key]) =>
      ['Expired', 'Cancelled'].includes(key)
    );
  }, [policyDetails.value]);

  if (policyDetails.loading) {
    return null;
  }

  return (
    <div className={styles['main-con']}>
      <H6
        text={
          Object.values(policyDetails.value).length <= 1
            ? policyDetailsCms.secondtitle
            : policyDetailsCms.title
        }
      />
      <div className={styles['insurance-con']}>
        <div className={styles['insurance-con-inner']}>
          {getAllPoliciesUtil(policies.value).length === 0 && <NoPolicyLink />}
          {Object.values(policyDetails.value).length > 0 && (
            <div className={styles['poilicies-con']}>
              {activeAndUpcomingPolicies.map(([key, value]) => {
                return (
                  <div key={key} className={styles['cards-con']}>
                    <Label text={`${key.toUpperCase()} POLICIES`} />
                    {/* {key === 'Active' && (
                      <div className={styles['info-con']}>
                        <IconExclamationCircleFilled
                          size={18}
                          color="var(--tang-blue-600)"
                        />
                        <BodyS text={policyDetailsCms.infoText} />
                      </div>
                    )} */}
                    {value.map((policy, index) => {
                      return (
                        <PolicyDetailsCard
                          key={index}
                          viewDetailsClick={async () => {
                            router.push(
                              ROUTER_GROUPS.policy.policyDetails(
                                await new DecryptedData(
                                  policy.policyNo
                                ).encrypt()
                              )
                            );
                            gtmModule.dashboard().sendSelectPromotion({
                              id: 'Policy details card',
                              name: 'View details',
                            });
                          }}
                          linkEkycClick={() =>
                            router.push(ROUTER_GROUPS.kyc.link(policy.policyId))
                          }
                          actionClick={(e) => actionClickHandler(e, policy)}
                          policy={policy}
                          cmsLabels={policyDetailsCms.policyCard}
                          actionGtmEvent={
                            gtmModule.dashboard().sendSelectPromotion
                          }
                          nicknameLabels={dashboardLabels.nicknameDump}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          {Object.values(policyDetails.value).length > 0 && <LinkPolicyComp />}
        </div>

        {expiredAndCancelledPolicies.map(([key, val]) => {
          return <ExpiredCancelledComp title={key} key={key} policies={val} />;
        })}
      </div>
    </div>
  );
}
